import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import AppPermissionsSwitcher from '../../../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../../Components/AppPermissionsSwitcher/constants/actions'
import { setDeleteCallback } from '../../../../Components/Modals/DeleteModal/utils'
import TableImage from '../../../../Components/TanstackTable/components/TableImage'
import { PrintableColumnDef } from '../../../../Components/TanstackTable'
import properties from '../../../../api/properties/hooks'
import { Property } from '../../../../api/properties/types/response'
import { PermissionsProps } from '../../../../api/globalTypes'
import { APPLICATION_SETTINGS, CONTACT_TYPE, MODAL_TYPES } from '../../../../common/constants'
import responseFields from '../../../../common/constants/responseFields'
import { projectResponseFields } from '../../../../common/response/project'
import { propertyResponseFields } from '../../../../common/response/property'
import { showModal } from '../../../../store/modal'
import { findEntityTitle, formatDateTime, formatNumberWithTwoDecimals } from '../../../../utils/general'
import { renderAreaUnit, renderCurrency, renderCurrencyWithUnit } from '../../../../utils/property'
import { getPropertyQuickViewData } from '../../utils'
import columnWidths from './columnWidths'

export const useTableColumns = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const location = useLocation()

    const currentLang = localStorage.getItem('i18nextLng') || 'hr'
    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT
    const currency = APPLICATION_SETTINGS.CURRENCY

    const w = useMemo(() => ({ ...columnWidths.hr, ...(columnWidths[currentLang] ?? {}) }), [currentLang])

    const { mutate: deleteProperty } = properties.DeleteProperty.useMutation()

    const handleQuickViewModal = useCallback(
        (data: Property) => {
            const filteredData = getPropertyQuickViewData(data)
            const payload = {
                modalType: MODAL_TYPES.QUICK_VIEW_MODAL,
                modalProps: {
                    data: filteredData,
                    appModule: 'property',
                },
            }

            dispatch(showModal(payload))
        },
        [dispatch]
    )

    const handleDelete = useCallback(
        (id: number) => {
            const callbackId = 'delete-property'
            const payload = {
                modalType: MODAL_TYPES.DELETE_MODAL,
                modalProps: { callbackId, id },
            }

            setDeleteCallback(callbackId, () => deleteProperty({ id }))
            dispatch(showModal(payload))
        },
        [dispatch, deleteProperty]
    )

    return useMemo(
        (): PrintableColumnDef<Property>[] => [
            {
                header: t('app.common.id'),
                id: propertyResponseFields.INTERNAL_APP_ID,
                accessorKey: propertyResponseFields.INTERNAL_APP_ID,
                enableSorting: true,
                size: w.internalAppId,
                meta: { exportId: propertyResponseFields.INTERNAL_APP_ID },
            },
            {
                header: t('table.header.common.mainPhoto'),
                id: 'mainPhoto',
                accessorFn: (property) => property.mainPhoto ?? null,
                enableSorting: false,
                size: w.mainPhoto,
                cell: ({ row }) => {
                    const mainPhoto = row.original.mainPhoto
                    if (!mainPhoto) return null

                    return (
                        <AppPermissionsSwitcher
                            permission="AllowedPhotos"
                            photo={mainPhoto}
                            module={location}
                            itemAgentId={row.original.primaryAgent?.id}
                            itemAgentOfficeId={row.original.primaryAgent?.agencyOffice?.id}
                        >
                            {({ photo }: { photo?: { id: number; updated: string; fileDescription?: string } }) =>
                                photo ? <TableImage photoId={photo.id} updated={photo.updated} alt={photo.fileDescription} /> : null
                            }
                        </AppPermissionsSwitcher>
                    )
                },
            },
            {
                header: t('app.common.agencyId'),
                id: propertyResponseFields.INTERNAL_ID,
                accessorKey: propertyResponseFields.INTERNAL_ID,
                enableSorting: true,
                size: w.internalId,
                meta: { exportId: propertyResponseFields.INTERNAL_ID },
            },
            {
                header: t('app.common.service'),
                id: propertyResponseFields.PROPERTY_SERVICE,
                accessorFn: (row) => row.propertyService?.name,
                enableSorting: true,
                size: w.propertyService,
                meta: { exportId: `${propertyResponseFields.PROPERTY_SERVICE}_name` },
            },
            {
                header: t('app.common.status'),
                id: propertyResponseFields.PROPERTY_STATUS,
                accessorFn: (row) => row.propertyStatus?.name,
                enableSorting: true,
                size: w.propertyStatus,
                meta: { exportId: `${propertyResponseFields.PROPERTY_STATUS}_name` },
            },
            {
                header: t('app.common.type'),
                id: propertyResponseFields.PROPERTY_TYPE,
                accessorFn: (row) => row.propertyType?.name,
                enableSorting: true,
                size: w.propertyType,
                meta: { exportId: `${propertyResponseFields.PROPERTY_TYPE}_name` },
            },
            {
                header: t('table.header.properties.subtype'),
                id: propertyResponseFields.PROPERTY_SUB_TYPE,
                accessorFn: (row) => row.propertySubType?.name,
                enableSorting: true,
                size: w.propertySubType,
                meta: { exportId: `${propertyResponseFields.PROPERTY_SUB_TYPE}_name` },
            },
            {
                header: t('app.common.country'),
                id: propertyResponseFields.COUNTRY,
                accessorFn: (row) => row.country?.name,
                enableSorting: true,
                size: w.country,
                meta: { exportId: `${propertyResponseFields.COUNTRY}_name` },
            },
            {
                header: t('app.common.county'),
                id: propertyResponseFields.COUNTY,
                accessorFn: (row) => row.county?.name,
                enableSorting: true,
                size: w.county,
                meta: { exportId: `${propertyResponseFields.COUNTY}_name` },
            },
            {
                header: t('app.common.city'),
                id: propertyResponseFields.CITY,
                accessorFn: (row) => row.city?.name,
                enableSorting: true,
                size: w.city,
                meta: { exportId: `${propertyResponseFields.CITY}_name` },
            },
            {
                header: t('app.common.widerArea'),
                id: propertyResponseFields.WIDER_AREA,
                accessorFn: (row) => row.widerArea?.name,
                enableSorting: true,
                size: w.widerArea,
                meta: { exportId: `${propertyResponseFields.WIDER_AREA}_name` },
                cell: ({ row, getValue }) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.property.widerArea"
                        itemAgentId={row.original.primaryAgent?.id}
                        itemAgentOfficeId={row.original.primaryAgent?.agencyOffice?.id}
                    >
                        {({ hasPermission }: PermissionsProps) => (hasPermission ? (getValue() as string) : null)}
                    </AppPermissionsSwitcher>
                ),
            },
            {
                header: t('app.common.quarter'),
                id: propertyResponseFields.QUARTER,
                accessorFn: (row) => row.quarter?.name,
                enableSorting: true,
                size: w.quarter,
                meta: { exportId: `${propertyResponseFields.QUARTER}_name` },
                cell: ({ row, getValue }) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.property.quarter"
                        itemAgentId={row.original.primaryAgent?.id}
                        itemAgentOfficeId={row.original.primaryAgent?.agencyOffice?.id}
                    >
                        {({ hasPermission }: PermissionsProps) => (hasPermission ? (getValue() as string) : null)}
                    </AppPermissionsSwitcher>
                ),
            },
            {
                header: t('app.common.street'),
                id: propertyResponseFields.STREET,
                accessorKey: propertyResponseFields.STREET,
                enableSorting: true,
                size: w.street,
                meta: { exportId: propertyResponseFields.STREET },
                cell: ({ row, getValue }) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.property.street"
                        itemAgentId={row.original.primaryAgent?.id}
                        itemAgentOfficeId={row.original.primaryAgent?.agencyOffice?.id}
                    >
                        {({ hasPermission }: PermissionsProps) => (hasPermission ? (getValue() as string) : null)}
                    </AppPermissionsSwitcher>
                ),
            },
            {
                header: t('app.common.streetNumber'),
                id: propertyResponseFields.STREET_NUMBER,
                accessorKey: propertyResponseFields.STREET_NUMBER,
                enableSorting: false,
                size: w.streetNumber,
                meta: { exportId: propertyResponseFields.STREET_NUMBER },
                cell: ({ row, getValue }) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.property.street"
                        itemAgentId={row.original.primaryAgent?.id}
                        itemAgentOfficeId={row.original.primaryAgent?.agencyOffice?.id}
                    >
                        {({ hasPermission }: PermissionsProps) => (hasPermission ? (getValue() as string) : null)}
                    </AppPermissionsSwitcher>
                ),
            },
            {
                header: t('app.common.price', { currency: renderCurrency(currency) }),
                id: propertyResponseFields.PRICE,
                accessorKey: propertyResponseFields.PRICE,
                enableSorting: true,
                size: w.price,
                meta: { exportId: propertyResponseFields.PRICE },
                cell: ({ row }) => {
                    const price = row.original[propertyResponseFields.PRICE as keyof Property] as number | null
                    return price ? formatNumberWithTwoDecimals(price) : null
                },
                printValue: (row) => {
                    const price = row?.[propertyResponseFields.PRICE as keyof Property] as number | null
                    return price ? formatNumberWithTwoDecimals(price) : ''
                },
            },
            {
                header: t('table.header.properties.pricePerUnit', { currencyPerUnit: renderCurrencyWithUnit(currency, areaUnit) }),
                id: propertyResponseFields.PRICE_M2,
                accessorKey: propertyResponseFields.PRICE_M2,
                enableSorting: true,
                size: w.priceM2,
                meta: { exportId: propertyResponseFields.PRICE_M2 },
                cell: ({ row }) => {
                    const priceM2 = row.original[propertyResponseFields.PRICE_M2 as keyof Property] as number | null
                    return priceM2 ? formatNumberWithTwoDecimals(priceM2) : null
                },
                printValue: (row) => {
                    const priceM2 = row?.[propertyResponseFields.PRICE_M2 as keyof Property] as number | null
                    return priceM2 ? formatNumberWithTwoDecimals(priceM2) : ''
                },
            },
            {
                header: t('app.common.area', { areaUnit: renderAreaUnit(areaUnit) }),
                id: propertyResponseFields.AREA,
                accessorKey: propertyResponseFields.AREA,
                enableSorting: true,
                size: w.area,
                meta: { exportId: propertyResponseFields.AREA },
                cell: ({ row }) => {
                    const area = row.original[propertyResponseFields.AREA as keyof Property] as number | null
                    return area ? formatNumberWithTwoDecimals(area) : null
                },
                printValue: (row) => {
                    const area = row?.[propertyResponseFields.AREA as keyof Property] as number | null
                    return area ? formatNumberWithTwoDecimals(area) : ''
                },
            },
            {
                header: t('app.common.agent'),
                id: propertyResponseFields.PRIMARY_AGENT_ID,
                accessorKey: propertyResponseFields.PRIMARY_AGENT_ID,
                enableSorting: true,
                size: w.primaryAgent,
                meta: {
                    fieldName: propertyResponseFields.PRIMARY_AGENT_ID,
                    exportId: ['primaryAgent_firstName', 'primaryAgent_lastName'],
                },
                cell: ({ row }) => (
                    <>
                        {row.original.primaryAgent?.firstName} {row.original.primaryAgent?.lastName}
                    </>
                ),
            },
            {
                header: t('table.header.properties.agentMobile'),
                id: 'agentMobile',
                accessorFn: (property) => property.primaryAgent.mobileNumber,
                enableSorting: false,
                size: w.agentMobile,
                meta: { fieldName: 'primaryAgent_mobileNumber', exportId: ['primaryAgent_mobileNumber'] },
            },
            {
                header: t('app.common.owner'),
                id: propertyResponseFields.OWNER,
                accessorKey: propertyResponseFields.OWNER,
                enableSorting: false,
                size: w.owner,
                meta: {
                    fieldName: propertyResponseFields.OWNER,
                    exportId: ['owner_0_contactPerson_firstName', 'owner_0_contactPerson_lastName'],
                },
                cell: ({ row }) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.property.owner"
                        itemAgentId={row.original.primaryAgent?.id}
                        itemAgentOfficeId={row.original.primaryAgent?.agencyOffice?.id}
                    >
                        {({ hasPermission }: PermissionsProps) =>
                            hasPermission ? (
                                <>
                                    {row.original.owner?.map((owner) => {
                                        const name =
                                            owner.contactType === CONTACT_TYPE.PERSON
                                                ? `${owner.contactPerson?.firstName ?? ''} ${owner.contactPerson?.lastName ?? ''}`.trim()
                                                : (owner.contactCompany?.name ?? '')
                                        return <div key={owner.id}>{name}</div>
                                    })}
                                </>
                            ) : null
                        }
                    </AppPermissionsSwitcher>
                ),
            },
            {
                header: t('table.header.properties.ownerPhoneNumber1'),
                id: `${propertyResponseFields.OWNER}PhoneNumber1`,
                accessorFn: (property) => property.owner?.[0]?.contactPhone?.[0]?.phoneNumber,
                enableSorting: false,
                size: w.ownerPhoneNumber1,
                meta: {
                    fieldName: 'owner_0_contactPhone_0_phoneNumber',
                    exportId: ['owner_0_contactPhone_0_phoneNumber'],
                },
                cell: ({ row, getValue }) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.property.owner"
                        itemAgentId={row.original.primaryAgent?.id}
                        itemAgentOfficeId={row.original.primaryAgent?.agencyOffice?.id}
                    >
                        {({ hasPermission }: PermissionsProps) => (hasPermission ? (getValue() as string) : null)}
                    </AppPermissionsSwitcher>
                ),
            },
            {
                header: t('table.header.properties.ownerPhoneNumber2'),
                id: `${propertyResponseFields.OWNER}PhoneNumber2`,
                accessorFn: (property) => property.owner?.[0]?.contactPhone?.[1]?.phoneNumber,
                enableSorting: false,
                size: w.ownerPhoneNumber2,
                meta: {
                    fieldName: 'owner_0_contactPhone_1_phoneNumber',
                    exportId: ['owner_0_contactPhone_1_phoneNumber'],
                },
                cell: ({ row, getValue }) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.property.owner"
                        itemAgentId={row.original.primaryAgent?.id}
                        itemAgentOfficeId={row.original.primaryAgent?.agencyOffice?.id}
                    >
                        {({ hasPermission }: PermissionsProps) => (hasPermission ? (getValue() as string) : null)}
                    </AppPermissionsSwitcher>
                ),
            },
            {
                header: t('app.common.floorNumber'),
                id: propertyResponseFields.FLOOR_NO,
                accessorKey: propertyResponseFields.FLOOR_NO,
                enableSorting: true,
                size: w.floorNo,
                meta: { exportId: propertyResponseFields.FLOOR_NO },
                cell: ({ row }) => {
                    const property = row.original
                    if (!property) return null

                    return (
                        <>
                            {property.propertyFloorPosition?.length > 0
                                ? property.propertyFloorPosition[0].name
                                : property.floorNo
                                  ? property.floorNo
                                  : '-'}
                            {property.floorsTotalNo ? `/${property.floorsTotalNo}` : '/-'}
                        </>
                    )
                },
            },
            {
                header: t('app.common.rooms'),
                id: propertyResponseFields.ROOMS_NO,
                accessorKey: propertyResponseFields.ROOMS_NO,
                enableSorting: true,
                size: w.roomsNo,
                meta: { exportId: propertyResponseFields.ROOMS_NO },
            },
            {
                header: t('app.common.bedrooms'),
                id: propertyResponseFields.BEDROOMS_NO,
                accessorKey: propertyResponseFields.BEDROOMS_NO,
                enableSorting: true,
                size: w.bedroomsNo,
                meta: { exportId: propertyResponseFields.BEDROOMS_NO },
            },
            {
                header: t('table.header.properties.maxConstructionArea', { areaUnit: renderAreaUnit(areaUnit) }),
                id: propertyResponseFields.MAX_CONSTRUCTION_AREA,
                accessorKey: propertyResponseFields.MAX_CONSTRUCTION_AREA,
                enableSorting: true,
                size: w.maxConstructionArea,
                meta: { exportId: propertyResponseFields.MAX_CONSTRUCTION_AREA },
                cell: ({ row }) => {
                    const value = row.original.maxConstructionArea
                    return value ? formatNumberWithTwoDecimals(value) : null
                },
                printValue: (row) => (row?.maxConstructionArea ? formatNumberWithTwoDecimals(row.maxConstructionArea) : ''),
            },
            {
                header: t('app.common.property.seaDistance'),
                id: propertyResponseFields.DISTANCE_SEA,
                accessorKey: propertyResponseFields.DISTANCE_SEA,
                enableSorting: true,
                size: w.distanceSea,
                meta: { exportId: propertyResponseFields.DISTANCE_SEA },
                cell: ({ row }) => {
                    const value = row.original.distanceSea
                    return value ? formatNumberWithTwoDecimals(value) : null
                },
                printValue: (row) => (row?.distanceSea ? formatNumberWithTwoDecimals(row.distanceSea) : ''),
            },
            {
                header: t('app.common.property.yard', { areaUnit: renderAreaUnit(areaUnit) }),
                id: propertyResponseFields.AREA_GARDEN,
                accessorKey: propertyResponseFields.AREA_GARDEN,
                enableSorting: true,
                size: w.areaGarden,
                meta: { exportId: propertyResponseFields.AREA_GARDEN },
                cell: ({ row }) => {
                    const value = row.original.areaGarden
                    return value ? formatNumberWithTwoDecimals(value) : null
                },
                printValue: (row) => (row?.areaGarden ? formatNumberWithTwoDecimals(row.areaGarden) : ''),
            },
            {
                header: t('app.common.property.areaPlot', { areaUnit: renderAreaUnit(areaUnit) }),
                id: propertyResponseFields.PLOT_AREA,
                accessorKey: propertyResponseFields.PLOT_AREA,
                enableSorting: true,
                size: w.plotArea,
                meta: { exportId: propertyResponseFields.PLOT_AREA },
                cell: ({ row }) => {
                    const value = row.original.plotArea
                    return value ? formatNumberWithTwoDecimals(value) : null
                },
                printValue: (row) => (row?.plotArea ? formatNumberWithTwoDecimals(row.plotArea) : ''),
            },
            {
                header: t('app.common.heating'),
                id: propertyResponseFields.PROPERTY_HEATING,
                enableSorting: false,
                size: w.propertyHeating,
                meta: { exportId: `${propertyResponseFields.PROPERTY_HEATING}_0_name` },
                cell: ({ row }) => (
                    <>
                        {row.original.propertyHeating?.map((item) => (
                            <div key={item.id}>{item.name}</div>
                        ))}
                    </>
                ),
            },
            {
                header: t('app.common.web'),
                id: propertyResponseFields.PROPERTY_WEBSITE,
                enableSorting: false,
                size: w.propertyWebsite,
                meta: { exportId: 'propertyWebsite_0_id' },
                cell: ({ row }) => {
                    if (row.original.propertyWebsite?.[0]?.visibility === 1) {
                        return <i className="mdi mdi-check fs-4" />
                    }
                    return null
                },
            },
            {
                header: t('table.header.properties.elevator'),
                id: propertyResponseFields.PROPERTY_EQUIPMENT,
                enableSorting: false,
                size: w.propertyEquipment,
                meta: { exportId: `${propertyResponseFields.PROPERTY_EQUIPMENT}_0_id` },
                cell: ({ row }) => {
                    const elevator = row.original.propertyEquipment?.find(({ id }) => id === 17)
                    if (elevator) {
                        return <i className="mdi mdi-check fs-4" />
                    }
                    return null
                },
            },
            {
                header: t('app.common.property.constructionYear'),
                id: propertyResponseFields.CONSTRUCTION_YEAR,
                accessorKey: propertyResponseFields.CONSTRUCTION_YEAR,
                enableSorting: true,
                size: w.constructionYear,
                meta: { exportId: propertyResponseFields.CONSTRUCTION_YEAR },
            },
            {
                header: t('app.common.property.availableFrom'),
                id: propertyResponseFields.AVAILABLE_FROM_DATE,
                accessorKey: propertyResponseFields.AVAILABLE_FROM_DATE,
                enableSorting: true,
                size: w.availableFromDate,
                meta: { exportId: propertyResponseFields.AVAILABLE_FROM_DATE },
                cell: ({ row }) => formatDateTime(row.original.availableFromDate),
            },
            {
                header: t('app.common.offerSource'),
                id: responseFields.PROPERTY_SUPPLY_SOURCE,
                accessorFn: (row) => row.propertySupplySource?.name,
                enableSorting: true,
                size: w.propertySupplySource,
                meta: { exportId: `${responseFields.PROPERTY_SUPPLY_SOURCE}_name` },
            },
            {
                header: t('view.common.other.created'),
                id: propertyResponseFields.CREATED,
                accessorKey: propertyResponseFields.CREATED,
                enableSorting: true,
                size: w.created,
                meta: { exportId: propertyResponseFields.CREATED },
                cell: ({ row }) => formatDateTime(row.original.created),
            },
            {
                header: t('app.common.contractNumber'),
                id: propertyResponseFields.CONTRACT_NO,
                accessorKey: propertyResponseFields.CONTRACT_NO,
                enableSorting: false,
                size: w.contractNo,
                meta: { exportId: propertyResponseFields.CONTRACT_NO },
            },
            {
                header: t('app.common.project.label'),
                id: propertyResponseFields.PROJECT,
                accessorFn: (property) => (property.project ? findEntityTitle(property.project, projectResponseFields.PROJECT_TITLE) : null),
                enableSorting: true,
                size: w.project,
                meta: { exportId: `project_translations_${currentLang}_projectTitle` },
                cell: ({ getValue }) => getValue(),
            },
            {
                header: t('app.common.property.designation'),
                id: propertyResponseFields.PROPERTY_LABEL,
                accessorKey: propertyResponseFields.PROPERTY_LABEL,
                enableSorting: false,
                size: w.propertyLabel,
                meta: { exportId: propertyResponseFields.PROPERTY_LABEL },
                cell: ({ row, getValue }) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.property.propertyLabel"
                        itemAgentId={row.original.primaryAgent?.id}
                        itemAgentOfficeId={row.original.primaryAgent?.agencyOffice?.id}
                    >
                        {({ hasPermission }: PermissionsProps) => (hasPermission ? (getValue() as string) : null)}
                    </AppPermissionsSwitcher>
                ),
            },
            {
                id: 'actions',
                enableSorting: false,
                size: w.actions,
                meta: { thDivClass: 'text-center', stopPropagation: true },
                cell: ({ row }) => {
                    const itemAgentId = row.original.primaryAgent?.id
                    const itemAgentOfficeId = row.original.primaryAgent?.agencyOffice?.id

                    return (
                        <ul className="list-inline hstack gap-2 mb-0 justify-content-center">
                            <li className="list-inline-item">
                                <Link
                                    to="#"
                                    title={t('button.view')}
                                    className="btn text-warning d-inline-block p-0"
                                    onClick={() => handleQuickViewModal(row.original)}
                                >
                                    <i className="mdi mdi-eye-outline fs-16"></i>
                                </Link>
                            </li>

                            <AppPermissionsSwitcher
                                permission="ModulePermissions"
                                itemAgentId={itemAgentId}
                                itemAgentOfficeId={itemAgentOfficeId}
                                module={location}
                                action={action.edit}
                            >
                                {({ hasModulePermission }: PermissionsProps) =>
                                    hasModulePermission && (
                                        <li className="list-inline-item edit">
                                            <Link
                                                title={t('button.edit')}
                                                to={`/properties/${row.original.id}/edit`}
                                                className="text-info d-inline-block edit-item-btn"
                                            >
                                                <i className="mdi mdi-pencil-outline fs-16"></i>
                                            </Link>
                                        </li>
                                    )
                                }
                            </AppPermissionsSwitcher>
                            <AppPermissionsSwitcher
                                permission="ModulePermissions"
                                itemAgentId={itemAgentId}
                                itemAgentOfficeId={itemAgentOfficeId}
                                module={location}
                                action={action.delete}
                            >
                                {({ hasModulePermission }: PermissionsProps) =>
                                    hasModulePermission && (
                                        <li className="list-inline-item edit">
                                            <Link
                                                title={t('button.delete')}
                                                to="#"
                                                className="text-danger d-inline-block remove-item-btn"
                                                onClick={() => handleDelete(row.original.id as number)}
                                            >
                                                <i className="mdi mdi-trash-can-outline fs-16"></i>
                                            </Link>
                                        </li>
                                    )
                                }
                            </AppPermissionsSwitcher>
                        </ul>
                    )
                },
            },
        ],
        [w, currentLang, areaUnit, currency, location, handleQuickViewModal, handleDelete, t]
    )
}

export default useTableColumns
